import React, { useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { DollarSign, MessageSquare, Calendar, Shield, Zap, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from './ui/tooltip';
import { Info } from 'lucide-react';
import { motion } from 'framer-motion';
import { useParallaxRef } from '@/hooks/useParallax';
import { useRevealOnce } from '@/hooks/useRevealOnce';
import { gridContainerVariants, getGridItemVariants } from '@/lib/animations';
interface PurchaseWaysSectionProps {
  variant?: 'homepage' | 'info' | 'domain';
  domainPrice?: number;
  className?: string;
  domain?: string;
  tld?: string;
  onModalOpen?: (modalType: 'offer' | 'bin') => void; // Legacy - for non-domain variants
  onPurchaseAction?: (actionType: 'offer' | 'lease' | 'cart') => void; // New - for domain variant
}
export default function PurchaseWaysSection({
  variant = 'homepage',
  domainPrice,
  className = '',
  domain,
  tld,
  onModalOpen,
  onPurchaseAction,
}: PurchaseWaysSectionProps) {
  // Parallax refs for title and subtitle
  const titleRef = useParallaxRef({ speed: -0.12, axis: 'y', maxTranslate: 60 });
  const subtitleRef = useParallaxRef({ speed: 0.08, axis: 'y', maxTranslate: 40 });

  // Use reveal once hook to prevent cards from disappearing after scroll
  const purchaseWaysReveal = useRevealOnce({ amount: 0.3 });

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };
  const purchaseWays = [
    {
      icon: DollarSign,
      title: 'Buy It Now',
      description:
        variant === 'domain'
          ? 'Immediate ownership with secure transfer via a trusted payment provider. I pay all fees.'
          : 'Get immediate ownership with a secure transfer through a trusted payment provider. I handle all fees and paperwork.',
      features: [
        'Instant ownership',
        'Secure via a trusted payment provider',
        'I pay all fees',
        '5-7 day transfer',
      ],
      price: domainPrice ? formatPrice(domainPrice) : 'Market Price',
      ctaText: 'Purchase Now',
      popular: true,
      action: 'cart' as const, // Explicit action for domain variant
      fallbackRoute: '/buy/bin', // Fallback for non-domain variants
    },
    {
      icon: Calendar,
      title: 'Lease (Use It Monthly)',
      description:
        variant === 'domain'
          ? 'Start using immediately with monthly payments. Buy anytime at the BIN price.'
          : 'Start using the domain immediately with flexible monthly payments. Buy out anytime.',
      features: ['Start immediately', 'Monthly payments', 'Buy anytime at BIN', 'Full DNS control'],
      price: domainPrice ? `${formatPrice(Math.ceil((domainPrice * 1.15) / 24))}/mo` : 'Monthly',
      ctaText: 'Lease to Own',
      popular: false,
      action: 'lease' as const, // Explicit lease action
      fallbackRoute: '/lease', // Fallback for non-domain variants
    },
    {
      icon: MessageSquare,
      title: 'Make an Offer',
      description:
        variant === 'domain'
          ? 'Negotiate terms that work for your budget. Include your use case for faster response.'
          : 'Submit a custom offer based on your budget and needs. I review all offers within 24 hours.',
      features: ['Flexible pricing', 'Custom terms', '24h response time', 'Use case considered'],
      price: 'Your Price',
      ctaText: 'Make Offer',
      popular: false,
      action: 'offer' as const, // Explicit offer action
      fallbackRoute: '/apply/offer', // Fallback for non-domain variants
    },
  ];
  const sectionTitle =
    variant === 'domain' ? 'Multiple Ways to Get This Domain' : 'Multiple Ways to Get Your Domain';
  const titleClass =
    variant === 'domain'
      ? 'text-5xl md:text-6xl font-bold mb-8 liquid-gradient-text'
      : 'text-6xl md:text-7xl font-bold mb-8 liquid-gradient-text drop-shadow-lg';
  const subtitleClass =
    variant === 'domain'
      ? 'text-xl text-muted-foreground max-w-4xl mx-auto leading-relaxed'
      : 'text-3xl md:text-4xl text-foreground/70 max-w-4xl mx-auto leading-relaxed';
  return (
    <section
      className={`py-24 ${variant === 'homepage' ? 'apple-section-bg' : 'bg-white'} ${className}`}
    >
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16 stagger-fade-1">
            <h2
              ref={titleRef as React.RefObject<HTMLHeadingElement>}
              className={titleClass}
            >
              {sectionTitle}
            </h2>
          </div>

          {/* Secure Transfers card removed per request */}

          {variant === 'domain' ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {purchaseWays.map((way, index) => (
                <div
                  key={index}
                  className={`apple-card p-8 relative ${way.popular ? 'ring-2 ring-primary/20 bg-primary/5' : ''}`}
                >
                  {way.popular && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2"></div>
                  )}

                  <div className="text-center mb-6">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                      <way.icon className="w-8 h-8 text-primary" />
                    </div>
                    <h3 className="text-2xl font-bold mb-2 text-black">{way.title}</h3>
                    <div className="text-3xl font-bold text-primary mb-4 flex items-center justify-center gap-1">
                      {way.price}
                      {way.title.includes('Lease') && domainPrice && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="w-4 h-4 text-muted-foreground cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent side="top" className="max-w-xs">
                            <p className="text-xs">
                              Starting at {way.price} (24-month term). Other terms available.
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </div>
                  </div>

                  <p className="text-muted-foreground mb-6 leading-relaxed">{way.description}</p>

                  <ul className="space-y-3 mb-8">
                    {way.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                        <span className="text-muted-foreground text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    className={`w-full ${way.popular ? 'bg-primary hover:bg-primary/90' : 'apple-button'}`}
                    size="lg"
                    onClick={() => {
                      if (variant === 'domain' && onPurchaseAction) {
                        // Domain variant: use explicit action from way config
                        onPurchaseAction(way.action);
                      } else {
                        // Non-domain variants: navigate to standalone pages
                        window.location.href = way.fallbackRoute;
                      }
                    }}
                  >
                    {way.ctaText}
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <motion.div
              ref={purchaseWaysReveal.ref}
              className="grid grid-cols-1 md:grid-cols-3 gap-8"
              variants={gridContainerVariants}
              initial="hidden"
              animate={purchaseWaysReveal.controls}
            >
              {purchaseWays.map((way, index) => (
                <motion.div
                  variants={getGridItemVariants(index)}
                  key={index}
                  className={`apple-card p-8 relative ${way.popular ? 'ring-2 ring-primary/20 bg-primary/5' : ''}`}
                >
                  {way.popular && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2"></div>
                  )}

                  <div className="text-center mb-6">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                      <way.icon className="w-8 h-8 text-primary" />
                    </div>
                    <h3 className="text-2xl font-bold mb-2 text-black">{way.title}</h3>
                    <div className="text-3xl font-bold text-primary mb-4 flex items-center justify-center gap-1">
                      {way.price}
                      {way.title.includes('Lease') && domainPrice && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="w-4 h-4 text-muted-foreground cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent side="top" className="max-w-xs">
                            <p className="text-xs">
                              Starting at {way.price} (24-month term). Other terms available.
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </div>
                  </div>

                  <p className="text-muted-foreground mb-6 leading-relaxed">{way.description}</p>

                  <ul className="space-y-3 mb-8">
                    {way.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                        <span className="text-muted-foreground text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    className={`w-full ${way.popular ? 'bg-primary hover:bg-primary/90' : 'apple-button'}`}
                    size="lg"
                    onClick={() => {
                      if (variant === 'domain' && onPurchaseAction) {
                        onPurchaseAction(way.action);
                      } else {
                        window.location.href = way.fallbackRoute;
                      }
                    }}
                  >
                    {way.ctaText}
                  </Button>
                </motion.div>
              ))}
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
}
